'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import BookingExtra from '@/components/modules/booking-extra/booking-extra';
import ModuleWrapper from '@/components/utilities/module-wrapper/module-wrapper';
import useQueryGenericProduct from '@/integrations/content/content-modules/generic-product/generic-product.hooks';
import { useBookingFlowContext } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import styles from './generic-product.module.scss';

export interface GenericProductProps {
  /** The UUID of the module */
  id: Content.Components['GenericProductHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['GenericProductHeadlessModuleDto']['pageId'];
  /** Booking parameters */
  parameters: Content.Components['GenericProductHeadlessModuleDto']['parameters'];
  /** Module settings*/
  settings: Content.Components['GenericProductHeadlessModuleDto']['settings'];
}

const GenericProduct = ({
  id,
  pageId,
  parameters,
  settings,
}: GenericProductProps) => {
  const componentRef = useRef<HTMLDivElement | null>(null);

  const {
    activeAnchor,
    anchorSignal,
    moduleStatusMap,
    setActiveAnchor,
    updateModuleStatus,
  } = useBookingFlowContext();

  const {
    data: dataGenericProduct,
    isFetching: isFetchingGenericProduct,
    isLoading: isLoadingGenericProduct,
  } = useQueryGenericProduct({
    parameters: parameters && {
      ...parameters,
      id,
      pageId,
    },
  });

  useEffect(() => {
    if (!dataGenericProduct) return;

    setActiveAnchor(null);
    updateModuleStatus(id, {
      isComplete: dataGenericProduct.isComplete,
      isRequired: dataGenericProduct.isRequired,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGenericProduct]);

  useEffect(() => {
    if (!componentRef.current) return;

    if (!activeAnchor) return;

    if (activeAnchor === id) {
      componentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [activeAnchor, anchorSignal, id]);

  const shouldHighlightErrors = useMemo(() => {
    return (
      activeAnchor === id &&
      !moduleStatusMap[id]?.isComplete &&
      moduleStatusMap[id]?.isRequired
    );
  }, [activeAnchor, id, moduleStatusMap]);

  const hasNoData = !isFetchingGenericProduct && !dataGenericProduct;

  if (hasNoData) return null;

  return (
    <ModuleWrapper
      ref={componentRef}
      className={styles.component}
      id={id}
      padding="none"
      settings={settings}
    >
      <BookingExtra
        {...dataGenericProduct}
        isFetching={isFetchingGenericProduct}
        isLoading={isLoadingGenericProduct}
        shouldHighlightErrors={shouldHighlightErrors}
      />
    </ModuleWrapper>
  );
};

GenericProduct.displayName = 'GenericProduct';

export default GenericProduct;
